import { hot } from "react-hot-loader/root";
import React, { useEffect } from "react";
import { Banner, Box, Button, Column, Columns } from "@sparkpost/matchbox";
import Section from "../components/Section";
import { i18n } from "../utils/utils";
import { getMetrics } from "../utils/ajax";
import { useAppState } from "../utils/context";
import { Refresh } from "@sparkpost/matchbox-icons";
import MetricsCharts from "../components/MetricsCharts";
import MetricFilters from "../components/MetricFilters";
import SendingProgress from "../components/SendingProgress";

const Metrics = () => {
  const { updateField, fields, isLoading, setLoading, displayNotice } =
    useAppState();

  //=====================================================================
  //
  //  ##      ##  #####  #####   ####  ##    ##   ####  ##      #####
  //  ##      ##  ##     ##     ##      ##  ##   ##     ##      ##
  //  ##      ##  #####  #####  ##       ####    ##     ##      #####
  //  ##      ##  ##     ##     ##        ##     ##     ##      ##
  //  ######  ##  ##     #####   ####     ##      ####  ######  #####
  //
  //=====================================================================

  /**
   * Refresh the metrics when switching to the Metrics tab or changing the filter range.
   */
  useEffect(() => {
    if (fields.license_activated && !isLoading) {
      fetchMetrics(true);
    }
  }, [fields.metricsFilter]);

  //=====================================================
  //
  //  #####  ##   ##  #####  ##     ##  ######   ####
  //  ##     ##   ##  ##     ####   ##    ##    ##
  //  #####  ##   ##  #####  ##  ## ##    ##     ###
  //  ##      ## ##   ##     ##    ###    ##       ##
  //  #####    ###    #####  ##     ##    ##    ####
  //
  //=====================================================

  /**
   * Fetch new metrics.
   */
  const fetchMetrics = async (force = false) => {
    if (isLoading) {
      return;
    }

    setLoading(true);
    const result = await getMetrics(force, fields.metricsFilter);

    if (result.success === true) {
      updateField("metrics", result.fields.metrics);

      if (result.fields.sending_limit) {
        updateField("sending_limit", result.fields.sending_limit);
      }
    } else {
      updateField("metrics", {});
      updateField("sending_limit", null);
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
  };

  /**
   * When manually refreshing.
   */
  const onRefreshClick = async (e) => {
    fetchMetrics(true);
  };

  /**
   * When changing filter.
   */
  const onMetricFilterChange = (range) => {
    updateField("metricsFilter", range);
  };

  //======================================
  //
  //  ##   ##    ###    #####     ####
  //  ##   ##   ## ##   ##  ##   ##
  //  ##   ##  ##   ##  #####     ###
  //   ## ##   #######  ##  ##      ##
  //    ###    ##   ##  ##   ##  ####
  //
  //======================================

  const data =
    fields.metrics && fields.metrics.data ? fields.metrics.data : null;

  const canShowData = !!fields.license_activated && !!fields.sending_domain;

  return (
    <>
      <Section className="section-metrics">
        {!canShowData ? (
          <Banner size="small" status="warning" onDismiss={() => {}}>
            {i18n("Text.NoMetricsReason")}
          </Banner>
        ) : (
          <header>
            {canShowData && (
              <>
                <MetricFilters
                  onChange={onMetricFilterChange}
                  current={fields.metricsFilter}
                />
                <Button
                  size="small"
                  variant="text"
                  color="blue"
                  onClick={onRefreshClick}
                  mutedOutline
                >
                  <Refresh size="36" fill="gray" />
                </Button>
              </>
            )}
          </header>
        )}
      </Section>
      {canShowData && fields.sending_limit && fields.sending_limit.max > 0 && (
        <SendingProgress progress={fields.sending_limit} />
      )}
      {data && canShowData && <MetricsCharts data={data} />}
    </>
  );
};

export default hot(Metrics);
